import EmptyIcon from '../assets/img/empty.png';
import {
  isValueInArray,
  removeValueFromArray,
  useOnClickOutside,
} from '../utils';
import { useMemo, useRef, useState } from 'react';
import {
  As,
  Box,
  Button,
  ButtonProps,
  Checkbox,
  CheckboxProps,
  CSSObject,
  Flex,
  Icon,
  Image,
  Input,
  Text,
  ThemeTypings,
  Tooltip,
  useMultiStyleConfig,
} from '@chakra-ui/react';
import {
  AiOutlineClose,
  AiOutlineDown,
  AiOutlineSearch,
} from 'react-icons/ai/index.js';

export interface OptionExtraProps {
  optionProps?: CSSObject;
  optionContainerProps?: CSSObject;
  optionIconProps?: CSSObject;
  optionLabelProps?: CSSObject;
}

export interface OptionProps
  extends Omit<ButtonProps, 'onSelect'>,
    OptionExtraProps {
  value: string | number;
  label: string;
  tooltip?: React.ReactNode;
  showCheckbox?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon?: As<any>;
  onSelect?: (value: string | number) => void;
  isSelected?: boolean;
  variant?: 'outline' | 'solid' | 'flushed' | 'unstyled';
  checkboxProps?: CheckboxProps;
  tooltipProps?: CSSObject;
}

export interface SelectProps extends OptionExtraProps {
  isMulti?: boolean;
  showSelectedOptions?: boolean;
  showCheckboxes?: boolean;
  name?: string;
  options?: OptionProps[];
  placeholder?: string;
  defaultValue?: string | number;
  defaultValues?: (string | number)[];
  variant?: 'outline' | 'solid' | 'flushed' | 'unstyled';
  onChange?: (value: string | number | (string | number)[]) => void;
  colorScheme?: ThemeTypings['colorSchemes'];
  showClearIcon?: boolean;
  showExpandIcon?: boolean;
  selectContainerProps?: CSSObject;
  searchBoxProps?: CSSObject;
  expandBtnProps?: CSSObject;
  expandIconProps?: CSSObject;
  optionsProps?: CSSObject;
  noOptionsText?: React.ReactNode;
  noOptionsProps?: CSSObject;
  noOptionsIconProps?: CSSObject;
  checkboxProps?: CheckboxProps;
  selectedOptionsContainerProps?: CSSObject;
  selectedOptionTagProps?: CSSObject;
  selectedOptionTextProps?: CSSObject;
  optionRemoveIconProps?: CSSObject;
  resetButtonProps?: CSSObject;
  resetIconProps?: CSSObject;
  isInvalid?: boolean;
  isDisabled?: boolean;
  hideSelectedItem?: boolean;
}

export const Option = (props: OptionProps) => {
  const {
    label,
    value,
    tooltip,
    icon,
    onSelect,
    isSelected,
    variant: _variant,
    colorScheme,
    optionProps,
    checkboxProps,
    optionContainerProps,
    optionIconProps,
    optionLabelProps,
    showCheckbox,
    tooltipProps,
    ...rest
  } = props;
  const styles = useMultiStyleConfig('Select', props);

  return (
    <Button
      onClick={() => onSelect && onSelect(value)}
      sx={{ ...styles.Option, ...optionProps }}
      {...rest}
    >
      <Tooltip
        label={tooltip}
        placement='top'
        sx={{ ...styles.Tooltip, ...tooltipProps }}
      >
        <Flex>
          {showCheckbox && (
            <Checkbox
              isChecked={isSelected}
              onChange={() => onSelect && onSelect(value)}
              colorScheme={colorScheme}
              sx={{ ...styles.Checkbox, ...checkboxProps }}
            />
          )}
          <Box sx={{ ...styles.OptionContainer, ...optionContainerProps }}>
            {icon && (
              <Icon
                as={icon}
                aria-hidden='true'
                sx={{ ...styles.OptionIcon, ...optionIconProps }}
              />
            )}
            <Text sx={{ ...styles.OptionLabel, ...optionLabelProps }}>
              {label}
            </Text>
          </Box>
        </Flex>
      </Tooltip>
    </Button>
  );
};

export const Select = (props: SelectProps) => {
  const {
    isMulti = false,
    showSelectedOptions = true,
    showCheckboxes = false,
    options = [],
    placeholder = 'Select...',
    onChange,
    defaultValue = '',
    defaultValues = [],
    variant = 'outline',
    colorScheme = 'gray',
    showClearIcon = true,
    showExpandIcon = true,
    name,
    selectContainerProps,
    searchBoxProps,
    expandBtnProps,
    expandIconProps,
    optionsProps,
    noOptionsText = 'No Options.',
    noOptionsProps,
    noOptionsIconProps,
    optionProps,
    optionContainerProps,
    optionIconProps,
    optionLabelProps,
    selectedOptionsContainerProps,
    selectedOptionTagProps,
    selectedOptionTextProps,
    optionRemoveIconProps,
    resetButtonProps,
    resetIconProps,
    checkboxProps,
    isDisabled = false,
    hideSelectedItem = true,
  } = props;
  const dropdownRef = useRef(null);
  const valueToNameMapping: { [value: string | number]: string } = {};

  const [showModal, setShowModal] = useState(false);
  const [hideValue, setHideValue] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const constructValueToNameMapping = () => {
    options.map((option) => {
      valueToNameMapping[option.value] = option.label;
    });
  };
  constructValueToNameMapping();

  const [selectedItem, setSelectedItem] = useState(defaultValue || '');
  const [selectedItems, setSelectedItems] = useState(defaultValues);
  const [inputText, setInputText] = useState(
    valueToNameMapping[selectedItem] || '',
  );

  const getOptionNames = useMemo(
    () => options.map((option) => option.label),
    [options],
  );

  const allOptionNames = getOptionNames;

  const getFilteredOptionNames = useMemo(
    () =>
      searchQuery === ''
        ? allOptionNames
        : allOptionNames.filter((optionName: string) => {
            return optionName.toLowerCase().includes(searchQuery.toLowerCase());
          }),
    [searchQuery, allOptionNames],
  );
  const filteredOptionNames = new Set(getFilteredOptionNames);
  const filteredOptions = useMemo(() => {
    return options.filter((option) => filteredOptionNames.has(option.label));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredOptionNames]);

  const handleSelectBoxItemClick = (value: string | number) => {
    setInputText(valueToNameMapping[value]);
    setSelectedItem(value);
    onChange && onChange(value);
    setShowModal(false);
  };

  const handleMultiSelectBoxItemClick = (
    value: string | number,
    clearText = false,
  ) => {
    let newSelectedItems = [];
    if (!isValueInArray(value, selectedItems)) {
      newSelectedItems = [...selectedItems, value];
      setSelectedItems([...newSelectedItems]);
    } else {
      newSelectedItems = removeValueFromArray(value, selectedItems);
      setSelectedItems([...newSelectedItems]);
    }
    if (clearText) {
      setSearchQuery('');
      setInputText('');
    }
    onChange && onChange(newSelectedItems);
  };

  const resetSelection = () => {
    setSelectedItems([]);
    onChange && onChange([]);
  };

  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, (e) => {
    const isTriggerElem = dropdownRef
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (dropdownRef.current as any)?.contains(e.target)
      : false;
    if (!isTriggerElem) {
      setShowModal(false);
    }
  });
  const styles = useMultiStyleConfig('Select', props);
  const searchBoxRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <Box
      ref={dropdownRef}
      className='select-box'
      sx={{ ...styles.SelectContainer, ...selectContainerProps }}
    >
      <Flex
        ref={containerRef}
        sx={{
          ...styles.SelectedOptionsContainer,
          ...selectedOptionsContainerProps,
        }}
        onFocus={() => {
          setHideValue(true);
          setSearchQuery('');
        }}
        onBlur={() => {
          setInputText(
            valueToNameMapping[selectedItem] ||
              valueToNameMapping[defaultValue],
          );
          setHideValue(false);
        }}
        onClick={() => {
          searchBoxRef.current?.focus();
          !isDisabled && !showModal && setShowModal(true);
        }}
      >
        {isMulti &&
          showSelectedOptions &&
          selectedItems.map((item, index) => (
            <Flex
              sx={{ ...styles.SelectedOptionTag, ...selectedOptionTagProps }}
              key={index}
            >
              <Text
                sx={{
                  ...styles.SelectedOptionText,
                  ...selectedOptionTextProps,
                }}
              >
                {valueToNameMapping[item]}
              </Text>
              <Icon
                as={AiOutlineClose}
                onClick={() => handleMultiSelectBoxItemClick(item)}
                sx={{ ...styles.RemoveIcon, ...optionRemoveIconProps }}
              />
            </Flex>
          ))}
        <Input
          type='text'
          isDisabled={isDisabled}
          placeholder={
            showModal || hideValue
              ? inputText
              : isMulti && selectedItems.length && showSelectedOptions
              ? ''
              : placeholder
          }
          value={hideValue ? '' : inputText}
          onChange={(e) => {
            const value =
              e.target.value ||
              `${selectedItem}` ||
              valueToNameMapping[defaultValue] ||
              '';
            setShowModal(true);
            setSearchQuery(e.target.value);
            setInputText(value);
            if (e.target.value) {
              hideValue && setHideValue(false);
            } else {
              setHideValue(true);
            }
          }}
          onKeyDown={(e) => {
            if (
              isMulti &&
              e.code === 'Backspace' &&
              selectedItems.length > 0 &&
              !searchQuery &&
              !inputText
            ) {
              handleMultiSelectBoxItemClick(
                selectedItems[selectedItems.length - 1],
              );
            }
          }}
          ref={searchBoxRef}
          onClick={() => {
            !isDisabled && setShowModal(!showModal);
          }}
          name={name}
          sx={{ ...styles.SearchBox, ...searchBoxProps }}
          spellCheck={false}
        />
      </Flex>
      {isMulti && showClearIcon && (
        <Button
          onClick={resetSelection}
          sx={{ ...styles.ClearButton, ...resetButtonProps }}
        >
          <Icon
            as={AiOutlineClose}
            aria-hidden='true'
            sx={{ ...styles.Icon, ...resetIconProps }}
          />
        </Button>
      )}
      {showExpandIcon && (
        <Button
          onClick={() => {
            showModal
              ? searchBoxRef.current?.blur()
              : searchBoxRef.current?.focus();
            setShowModal(!showModal);
          }}
          sx={{ ...styles.ToggleButton, ...expandBtnProps }}
          isDisabled={isDisabled}
        >
          <Icon
            as={showModal ? AiOutlineSearch : AiOutlineDown}
            aria-hidden='true'
            sx={{ ...styles.Icon, ...expandIconProps }}
          />
        </Button>
      )}
      {showModal && (
        <Box
          ref={modalRef}
          sx={{
            ...styles.OptionsModal,
            top:
              (containerRef.current?.getBoundingClientRect().top || 0) +
              (containerRef.current?.getBoundingClientRect().height || 0),
            left: containerRef.current?.getBoundingClientRect().left || 0,
            ...optionsProps,
          }}
        >
          {filteredOptions.length ? (
            filteredOptions.map((option, index) => (
              <Option
                isSelected={
                  isMulti
                    ? isValueInArray(option.value, selectedItems)
                    : selectedItem === option.value
                }
                onSelect={(value) => {
                  isMulti
                    ? handleMultiSelectBoxItemClick(value, true)
                    : handleSelectBoxItemClick(value);
                  setHideValue(false);
                }}
                showCheckbox={showCheckboxes}
                key={index}
                variant={variant}
                colorScheme={colorScheme}
                optionProps={optionProps}
                optionContainerProps={optionContainerProps}
                optionIconProps={optionIconProps}
                optionLabelProps={optionLabelProps}
                checkboxProps={checkboxProps}
                {...option}
                hidden={
                  option.hidden ||
                  (hideSelectedItem && option.value === selectedItem)
                }
              />
            ))
          ) : (
            <Box sx={{ ...styles.NoOptions, ...noOptionsProps }}>
              <Image
                src={EmptyIcon}
                sx={{ ...styles.NoOptionsIcon, ...noOptionsIconProps }}
              />
              {noOptionsText}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
